import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

function ContributionBreakdown({ chart_data }) {
  const [view, setView] = useState("monthly");
  const [displayType, setDisplayType] = useState("chart");

  const [monthlyData, setMonthlyData] = useState({});
  const [quarterlyData, setQuarterlyData] = useState({});

  const [data, setData] = useState({});
  const [options, setOptions] = useState({});

  // ================= PREPARE DATA =================
  useEffect(() => {
    if (!chart_data || chart_data.length === 0) return;

    // -------- MONTHLY --------
    let months = [];
    let monthlyRevenue = [];
    let monthlyCollection = [];

    chart_data.forEach((item) => {
      months.push(item.month_name);

      monthlyRevenue.push(
        Number(
          ((item.total_revenue || 0) / 10000000).toFixed(2)
        )
      );

      monthlyCollection.push(
        Number(
          (
            (item.collection_amount || 0) / 10000000
          ).toFixed(2)
        )
      );
    });

    setMonthlyData({
      labels: months,
      revenue: monthlyRevenue,
      collection: monthlyCollection,
    });

    // -------- QUARTERLY --------
    let quarterMap = {};

    chart_data.forEach((item) => {
      let q = item.quarter;

      let revenue =
        (item.total_revenue || 0) / 10000000;

      let collection =
        (item.collection_amount || 0) / 10000000;

      if (!quarterMap[q]) {
        quarterMap[q] = {
          revenue: 0,
          collection: 0,
        };
      }

      quarterMap[q].revenue += revenue;

      quarterMap[q].collection += collection;
    });

    const quarterLabels = Object.keys(quarterMap);

    const quarterRevenue = quarterLabels.map((q) =>
      Number(quarterMap[q].revenue.toFixed(2))
    );

    const quarterCollection = quarterLabels.map((q) =>
      Number(quarterMap[q].collection.toFixed(2))
    );

    setQuarterlyData({
      labels: quarterLabels,
      revenue: quarterRevenue,
      collection: quarterCollection,
    });
  }, [chart_data]);

  // ================= CHART CONFIG =================
  useEffect(() => {
    const selectedData =
      view === "monthly"
        ? monthlyData
        : quarterlyData;

    if (!selectedData?.labels) return;

    setData({
      labels: selectedData.labels,

      datasets: [
        {
          label: "Business Delivered",

          data: selectedData.revenue,

          backgroundColor: "#36A2EB",

          borderRadius: 12,
        },

        {
          label: "Collection Amount",

          data: selectedData.collection,

          backgroundColor: "#3ad35b",

          borderRadius: 12,
        },
      ],
    });

    setOptions({
      responsive: true,

      maintainAspectRatio: false,

      plugins: {
        legend: {
          display: true,
          position: "top",
        },

        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ₹ ${Number(
                context.raw
              ).toFixed(2)} Cr`,
          },
        },
      },

      scales: {
        y: {
          beginAtZero: true,

          ticks: {
            callback: (value) =>
              `₹ ${Number(value).toFixed(2)} Cr`,
          },
        },
      },
    });
  }, [view, monthlyData, quarterlyData]);

  // ================= TABLE DATA =================
  const tableData =
    view === "monthly"
      ? chart_data
      : Object.values(
          chart_data.reduce((acc, item) => {
            if (!acc[item.quarter]) {
              acc[item.quarter] = {
                quarter: item.quarter,
                total_revenue: 0,
                collection_amount: 0,
              };
            }

            acc[item.quarter].total_revenue += Number(
              item.total_revenue || 0
            );

            acc[item.quarter].collection_amount +=
              Number(item.collection_amount || 0);

            return acc;
          }, {})
        );

  return (
    <div style={{ width: "100%" }}>
      {/* ================= TOGGLES ================= */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: 15,
        }}
      >
        {/* LEFT SIDE */}
        <div>
          {["monthly", "quarterly"].map((type) => (
            <button
              key={type}
              onClick={() => setView(type)}
              style={{
                padding: "6px 18px",
                marginRight: 8,
                borderRadius: 25,
                border: "none",

                background:
                  view === type
                    ? "#1E63E9"
                    : "#ddd",

                color:
                  view === type
                    ? "#fff"
                    : "#333",

                cursor: "pointer",

                textTransform: "capitalize",
              }}
            >
              {type}
            </button>
          ))}
        </div>

        {/* RIGHT SIDE */}
        <div>
          {["chart", "table"].map((type) => (
            <button
              key={type}
              onClick={() => setDisplayType(type)}
              style={{
                padding: "6px 18px",
                marginLeft: 8,
                borderRadius: 25,
                border: "none",

                background:
                  displayType === type
                    ? "#198754"
                    : "#ddd",

                color:
                  displayType === type
                    ? "#fff"
                    : "#333",

                cursor: "pointer",

                textTransform: "capitalize",
              }}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* ================= CHART ================= */}
      {displayType === "chart" ? (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "300px",
          }}
        >
          {data?.datasets && (
            <Bar data={data} options={options} />
          )}
        </div>
      ) : (
        /* ================= TABLE ================= */
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead>
              <tr>
                <th>
                  {view === "monthly"
                    ? "Month"
                    : "Quarter"}
                </th>

                <th>Business Delivered</th>

                <th>Collection Amount</th>
              </tr>
            </thead>

            <tbody>
              {tableData.map((item, index) => (
                <tr key={index}>
                  <td>
                    {view === "monthly"
                      ? item.month_name
                      : item.quarter}
                  </td>

                  <td>
                    ₹{" "}
                    {Number(
                      (item.total_revenue || 0) /
                        10000000
                    ).toFixed(2)}{" "}
                    Cr
                  </td>

                  <td>
                    ₹{" "}
                    {Number(
                      (item.collection_amount || 0) /
                        10000000
                    ).toFixed(2)}{" "}
                    Cr
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ContributionBreakdown;